import React from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Loader2, CheckCircle2, Bell } from "lucide-react";

export const WaitlistSection = () => {
  const { t } = useTranslation();
  const [city, setCity] = React.useState("paris");
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error(t("waitlist.err_required"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/waitlist", { city, name: name.trim(), email: email.trim(), phone: phone.trim() });
      setDone(true);
      toast.success(t("waitlist.success"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("waitlist.err_generic"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="section" data-testid="waitlist-section" id="waitlist">
      <div className="container-narrow">
        <div className="rounded-3xl bg-gradient-to-br from-[#1B2845] to-[#293B61] p-8 sm:p-12 text-white">
          <div className="max-w-2xl">
            <p className="eyebrow !text-[#5BA4D4] flex items-center gap-2">
              <Bell className="h-4 w-4" /> {t("waitlist.eyebrow")}
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold">{t("waitlist.title")}</h2>
            <p className="mt-4 text-slate-200">{t("waitlist.subtitle")}</p>
          </div>

          {done ? (
            <div className="mt-8 flex items-center gap-3 rounded-2xl bg-white/10 p-6" data-testid="waitlist-done">
              <CheckCircle2 className="h-8 w-8 text-emerald-400 flex-shrink-0" />
              <div>
                <p className="font-semibold">{t("waitlist.success")}</p>
                <p className="text-sm text-slate-300">{t("waitlist.success_sub")}</p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8" data-testid="waitlist-form">
              {/* City choice */}
              <div className="flex gap-3 mb-4">
                {["paris", "bergamo"].map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCity(c)}
                    className={`flex-1 rounded-xl border-2 py-3 px-4 font-semibold transition flex items-center justify-center gap-2 ${
                      city === c ? "border-[#5BA4D4] bg-[#5BA4D4]/15 text-white" : "border-white/20 text-slate-300 hover:border-white/40"
                    }`}
                    data-testid={`waitlist-city-${c}`}
                  >
                    <MapPin className="h-4 w-4" /> {c === "paris" ? "Paris" : "Bergamo"}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("waitlist.name")}
                  className="rounded-lg px-3 py-2.5 text-navy text-sm outline-none focus:ring-2 focus:ring-[#5BA4D4]"
                  data-testid="waitlist-name"
                />
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  placeholder={t("waitlist.email")}
                  className="rounded-lg px-3 py-2.5 text-navy text-sm outline-none focus:ring-2 focus:ring-[#5BA4D4]"
                  data-testid="waitlist-email"
                />
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t("waitlist.phone")}
                  className="rounded-lg px-3 py-2.5 text-navy text-sm outline-none focus:ring-2 focus:ring-[#5BA4D4]"
                  data-testid="waitlist-phone"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="mt-4 rounded-xl bg-[#5BA4D4] hover:bg-[#4a93c3] text-white font-semibold px-6 py-3 flex items-center gap-2 disabled:opacity-50 transition"
                data-testid="waitlist-submit"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
                {t("waitlist.submit")}
              </button>
              <p className="mt-3 text-xs text-slate-400">{t("waitlist.privacy")}</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default WaitlistSection;
